/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  filterPeopleByRole,
  IConfigurationModel,
  ImageHelper,
  IMediaModel,
  MediaType,
  PersonInAssetType,
  RouteHelper,
  TimeHelper,
} from "@nf/common";
import React, { useMemo } from "react";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { OpenGraphVideo } from "next-seo/lib/types";
import Head from "next/head";
import { useMediaMetaDescription, useMediaMetaTitle } from "./utils";
import { useCountry } from "hooks";
import { useLanguage } from "hooks/useLanguage";

export interface IMediaSeoProps {
  media: IMediaModel;
  configuration?: IConfigurationModel;
}

export const MediaSEO = ({ media, configuration }: IMediaSeoProps) => {
  const router = useRouter();
  const country = useCountry();
  const language = useLanguage();

  const imageUrl =
    ImageHelper.getHighlightsImageUrl(media?.Images) ||
    ImageHelper.getHeroAssetDetailsImageUrl(media?.Images);
  const href = process.env.NEXT_PUBLIC_MAIN_DOMAIN + router.asPath;
  const mainCanonicalUrl = RouteHelper.getMainCanonicalUrl();
  const canonicalUrl = RouteHelper.getMediaCanonicalUrl(
    router.pathname,
    media,
    configuration
  );

  const openGraphType = useMemo(() => {
    switch (media?.MediaTypeCode) {
      case MediaType.Video:
        return "video.movie";
      case MediaType.Series:
        return "video.tv_show";
      case MediaType.Episode:
        return "video.episode";
      default:
        return "video.other";
    }
  }, [media]);

  const openGraphVideo = useMemo(() => {
    const video: OpenGraphVideo = {};

    const filteredActors = filterPeopleByRole(
      media.People,
      PersonInAssetType.Actor
    );
    const actorProfiles = filteredActors?.map((actor) => ({
      profile: actor.PersonFullName,
    }));
    if (actorProfiles && !!actorProfiles.length) {
      video.actors = [...actorProfiles];
    }

    const directors = filterPeopleByRole(
      media.People,
      PersonInAssetType.Director
    )?.map((director) => director.PersonFullName);

    if (directors && !!directors.length) {
      video.directors = directors;
    }

    const filteredTags = media?.Categories?.map(
      (category) => category.CategoryName
    );
    if (filteredTags && !!filteredTags.length) {
      video.tags = [...filteredTags];
    }

    if (media?.Duration) {
      video.duration = media?.Duration;
    }

    if (media?.AvailableFrom) {
      video.releaseDate = TimeHelper.toISOString(media?.AvailableFrom);
    }

    return video;
  }, [media]);

  const metaTitle = useMediaMetaTitle(media);
  const metaDescription = useMediaMetaDescription(media);

  const addLdJson = () => {
    if (media) {
      const title = media?.Title;
      const description =
        media?.Description ||
        media?.ShortDescription ||
        media?.LongDescription ||
        media?.Title;

      const trialPath = RouteHelper.goToTrial(media.Id);
      const trialUrl = `${mainCanonicalUrl}/${country}/${language}${trialPath.pathname}?${trialPath.query}`;

      return {
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Movie",
          name: title,
          description: description,
          image: imageUrl,
          dateCreated: `${media?.Year}` ?? "",
          director: openGraphVideo.directors?.map((director) => ({
            "@type": "Person",
            name: director ?? "",
          })),
          actor: openGraphVideo?.actors?.map((actor) => ({
            "@type": "Person",
            name: actor.profile ?? "",
          })),
          trailer: media.TrailerMediaId
            ? {
                "@type": "VideoObject",
                name: `Official Trailer ${title}`,
                description: description,
                dateCreated: `${media?.Year}` ?? "",
                uploadDate: openGraphVideo.releaseDate,
                thumbnailUrl: imageUrl ?? "",
                embedUrl: trialUrl,
              }
            : undefined,
        }),
      };
    }
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addLdJson()}
        />
        <title>{metaTitle}</title>
      </Head>
      <NextSeo
        title={metaTitle}
        titleTemplate="%s"
        description={metaDescription}
        canonical={canonicalUrl}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: href,
          site_name: process.env.NEXT_PUBLIC_NAME,
          type: openGraphType,
          video: openGraphVideo,
          images: [{ url: imageUrl ?? "" }],
        }}
      />
    </>
  );
};
